import React, { useEffect, useState } from 'react';
import { NanoFooter, NanoContentLinks, NanoSelect, NanoOption, NanoIcon } from '@nanoporetech-digital/components-react';
import useStoryblokGlobalFooter from './useStoryblokGlobalFooter';
import { getResolvedStoryblokUrl } from '../MegaDropDown/helpers';
import UiMarkdown from '../Ui/UiMarkdown';
import CurrencySwitcher from '../WebStore/CurrencySwitcher';
import { isChinaStore } from '../../hooks/siteData';
import iso27001 from './assets/iso-27001.svg';
import iso9001 from './assets/iso-9001.svg';
import bsiMark from './assets/bsi-mark-of-trust.svg';
import * as styles from './style.module.css';

// The three top columns of the design-system footer, in slot order.
const TOP_SLOTS = ['top-start', 'top-center', 'top-end'];

const ACCREDITATIONS = [
    { href: 'http://nanoporetech.com/commercial_information#tabs-0=policies', src: iso27001, alt: 'ISO 27001' },
    { href: 'http://nanoporetech.com/commercial_information#tabs-0=policies', src: iso9001, alt: 'ISO 9001' },
];

const LANGUAGES = [
    { value: 'zh', href: 'http://nanoporetech.net/', label: '中文', flag: 'cn', domain: 'nanoporetech.net' },
    { value: 'ja', href: 'http://nanoporetech.jp/', label: '日本語', flag: 'jp', domain: 'nanoporetech.jp' },
    { value: 'en', href: 'http://nanoporetech.com/', label: 'English', flag: 'gb', domain: 'nanoporetech.com' },
];

const DEFAULT_LANGUAGE = 'en';

export default function Footer() {
    const globalFooter = useStoryblokGlobalFooter();

    // Detect the active language from the current domain, falling back to English.
    // Resolved after mount to avoid an SSR/hydration mismatch.
    const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
    useEffect(() => {
        const host = window.location.hostname;
        const match = LANGUAGES.find(lang => host.endsWith(lang.domain));
        if (match) setCurrentLanguage(match.value);
    }, []);

    const currentFlag = LANGUAGES.find(lang => lang.value === currentLanguage)?.flag;

    const menus = (globalFooter.menu_primary ?? [])
        .filter(item => item.component === 'menu')
        .slice(0, TOP_SLOTS.length);

    return (
        <NanoFooter>
            {menus.map((menu, i) => {
                const slot = TOP_SLOTS[i];
                return (
                    <React.Fragment key={menu._uid ?? i}>
                        {menu.title && <h4 slot={`${slot}_heading`}>{menu.title}</h4>}
                        {(menu.items?.length > 0 || i === 0) && (
                            <NanoContentLinks slot={`${slot}_content`}>
                                {(menu.items ?? [])
                                    .filter(item => !!item.label && (!!item.link?.url || !!item.link?.cached_url))
                                    .map((item, j) => (
                                        <MenuLink key={item._uid ?? j} item={item} />
                                    ))}
                                
                                {i === 0 && <CurrencySwitcher />}
                            </NanoContentLinks>
                        )}
                    </React.Fragment>
                );
            })}

            {ACCREDITATIONS.map(acc => (
                <a slot="middle-end" key={acc.alt} href={acc.href} target="_blank" rel="noopener noreferrer">
                    <img src={acc.src} alt={acc.alt} loading="lazy" />
                </a>
            ))}

            {(globalFooter.legal_info || isChinaStore()) && (
                <div slot="bottom-start" className="footer__legal-text">
                  {globalFooter.legal_info && <UiMarkdown>{globalFooter.legal_info}</UiMarkdown>}
                  {isChinaStore() && <p>Shanghai ICP number 沪ICP备20017565号</p>}
                </div>
            )}

            <NanoSelect
                slot="bottom-end"
                hideLabel
                label="Language / region"
                placeholder="Language / region"
                value={currentLanguage}
                className="nano-theme-dark"
                style={{ '--input-border-color': 'none' }}
            >
                {currentFlag && <NanoIcon slot="start" name={`flag/${currentFlag}`} style={{ fontSize: 'var(--nano-pictogram-size-small)' }} />}
                {LANGUAGES.map(lang => (
                    <NanoOption key={lang.value} value={lang.value} href={lang.href} label={lang.label}>
                        <NanoIcon slot="start" name={`flag/${lang.flag}`} style={{ fontSize: 'var(--nano-pictogram-size-small)', '--padding-end': 0 }} />
                        {lang.label}
                    </NanoOption>
                ))}
            </NanoSelect>
        </NanoFooter>
    );
}

function MenuLink({ item }) {
    return (
        <a href={getResolvedStoryblokUrl(item.link)} target={item.link?.target || undefined}>
            {item.label}
            {item.sub_text && <span className={styles.subText}>{item.sub_text}</span>}
        </a>
    );
}
